package pl.decks.personal.components.widgets.ranks.military

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times
import pl.decks.personal.components.widgets.ranks.RanksStylesheet
import pl.decks.personal.components.widgets.ranks.RanksStylesheet.Shapes
import pl.decks.personal.components.widgets.ranks.RanksStylesheet.Vars
import pl.decks.personal.components.widgets.ranks.shapes.Badge
import pl.decks.personal.components.widgets.ranks.shapes.RankChevron
import pl.decks.personal.components.widgets.ranks.shapes.RankLine
import pl.decks.personal.components.widgets.ranks.shapes.RankStar
import pl.decks.personal.styles.SiteLayout
import pl.decks.personal.theme.SitePalette

@Composable
internal fun ParaMilitaryBadge(
    rank: ParaMilitaryRank,
    narrowingWidth: Float,
    narrowingHeight: Float,
    coloring: SitePalette.MilitaryBadgeColors,
    modifier: Modifier = Modifier,
) = Box(RanksStylesheet.SlotBadge.toModifier().then(modifier), contentAlignment = Alignment.Center) {
    Badge(
        narrowingWidth = narrowingWidth,
        narrowingHeight = narrowingHeight,
        backgroundColor = coloring.badge,
        strokeColor = coloring.stroke,
    )
    BadgeInsignia(
        rank,
        narrowingWidth,
        coloring.insignia,
    )
}

@Composable
private fun BadgeInsignia(
    rank: ParaMilitaryRank,
    narrowingWidth: Float,
    color: Color,
    modifier: Modifier = Modifier,
) = Box(
    Modifier
        .fillMaxHeight()
        .fillMaxWidth(SiteLayout.Fraction.All * (1 - 2 * narrowingWidth))
        .then(modifier),
    contentAlignment = Alignment.Center,
) {
    val baseGap = when (rank) {
        ParaMilitaryRank.PRIVATE1,
        ParaMilitaryRank.PRIVATE2,
        ParaMilitaryRank.PRIVATE3,
        ParaMilitaryRank.SERGEANT1
            -> Vars.Insignia.lineLineRelGap.value()

        else -> SiteLayout.Space.none
    }
    InsigniaElementsContainer(baseGap) {
        InsigniaElements(rank, color)
    }
}

@Composable
private fun InsigniaElements(rank: ParaMilitaryRank, color: Color) = when (rank) {
    ParaMilitaryRank.PRIVATE1 -> {
        Line(color)
    }

    ParaMilitaryRank.PRIVATE2 -> {
        Line(color)
        Line(color)
    }

    ParaMilitaryRank.PRIVATE3 -> {
        Line(color)
        Line(color)
        Line(color)
    }

    ParaMilitaryRank.SERGEANT1 -> {
        Chevron(color)
        Line(color)
        LineInsigniaSpace(addGap = SiteLayout.Space.none)
        LineInsigniaSpace(addGap = SiteLayout.Space.none)
    }

    ParaMilitaryRank.SERGEANT2 -> {
        Chevron(color)
        Chevron(color)
        LineInsigniaSpace()
        LineInsigniaSpace()
        LineInsigniaSpace()
    }

    ParaMilitaryRank.SERGEANT3 -> {
        Star(color)
        Chevron(color)
        LineInsigniaSpace()
        LineInsigniaSpace()
        LineInsigniaSpace()
    }

    ParaMilitaryRank.ENSIGN1 -> {
        Star(color)
        ChevronInsigniaSpace()
        LineInsigniaSpace()
        LineInsigniaSpace()
        LineInsigniaSpace()
    }

    ParaMilitaryRank.ENSIGN2 -> {
        Star(color)
        ChevronInsigniaSpace { Star(color) }
        LineInsigniaSpace()
        LineInsigniaSpace()
        LineInsigniaSpace()
    }

    ParaMilitaryRank.ENSIGN3 -> {
        LineInsigniaSpace { Star(color) }
        LineInsigniaSpace()
        Star(color)
        ChevronInsigniaSpace { Star(color) }
        LineInsigniaSpace()
        LineInsigniaSpace { Star(color) }
        LineInsigniaSpace()
    }
}

@Composable
private fun InsigniaElementsContainer(
    elementsGap: CSSLengthOrPercentageNumericValue = SiteLayout.Space.none,
    content: @Composable () -> Unit,
) = Column(
    Modifier.fillMaxSize().padding(topBottom = Vars.Insignia.topBottomPadding.value()).gap(elementsGap),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    Box(Modifier.weight(1f))
    content()
}

@Composable
private fun Line(color: Color) = RankLine(color = color, modifier = Shapes.Line.toModifier())

@Composable
private fun Chevron(color: Color) = RankChevron(color = color, modifier = Shapes.Chevron.toModifier())

@Composable
private fun Star(color: Color) = RankStar(color = color, modifier = Shapes.Star.toModifier())

@Composable
private fun ChevronInsigniaSpace(
    content: @Composable () -> Unit = {},
) = InsigniaSpace(
    height = Vars.Insignia.chevronRelSize.value(),
    content = content,
)

@Composable
private fun LineInsigniaSpace(
    addGap: CSSLengthOrPercentageNumericValue? = null,
    content: @Composable () -> Unit = {},
) = InsigniaSpace(
    height = Vars.Insignia.lineRelSize.value() + (addGap ?: Vars.Insignia.lineLineRelGap.value()),
    content = content,
)

@Composable
private fun InsigniaSpace(
    height: CSSLengthOrPercentageNumericValue,
    content: @Composable () -> Unit = {},
) = Box(Modifier.fillMaxWidth().height(height), contentAlignment = Alignment.Center) {
    content()
}
